import {
  asCurrency,
  getFixedMortgageAmortization,
  getMonthlyPayment,
  normalizedDate,
  updateValuesFromFields,
  validateFields,
} from '../util'
import { useAppState } from '../Context'
import { EquityBalanceInterestChart } from '../components/Charts'
import { AmortizationScheduleTable } from '../components/Tables'
import { InputFields } from '../components/Fields'
import CalculateResults from '../components/CalculateResults'
import addMonths from 'date-fns/addMonths'
import currency from 'currency.js'

const getTotalRentCost = (payment, rate, numMonths) => {
  let total = currency(0)
  let savings = currency(0)
  let paymentAmount = currency(payment)

  for (let i = 0; i < numMonths; i++) {
    if (i > 0 && i % 12 === 0) {
      paymentAmount = paymentAmount.add(paymentAmount.multiply(rate / 100))
    }

    total = total.add(paymentAmount)
  }

  return total.subtract(savings)
}

const getTotalEquity = (values) => {
  let totalValue = values.amortization.totalPrincipal.add(values.downPayment)

  for (let i = 0; i < values.stayMonths; i++) {
    if (i > 0 && i % 12 === 0) {
      totalValue = totalValue.add(totalValue.multiply(values.appreciationRate / 100))
    }
  }

  const homeEquity = values.amortization.paymentSchedule.map((event) => totalValue - event.balance.value)

  const total = homeEquity[values.stayMonths - 1]

  return total
}

const getBuyTotals = (values) => {
  const monthlyPayment = getMonthlyPayment(
    currency(values.homeCost).subtract(values.downPayment),
    values.interestRate,
    values.numPayments
  )

  const propertyTax = currency(values.homeCost)
    .multiply(values.propertyTax / 100)
    .divide(12)

  const totalMonthlyPayment = currency(monthlyPayment)
    .add(propertyTax)
    .add(values.dues)
    .add(values.mortgageInsurance)
    .add(values.homeownerInsurance)
    .add(values.maintenance)

  let total = currency(0)
  let savings = currency(0)
  let paymentAmount = currency(totalMonthlyPayment)

  for (let i = 0; i < values.stayMonths; i++) {
    total = total.add(paymentAmount)
  }

  return {
    presentValue: total.add(values.downPayment).subtract(savings),
  }
}

function PanelInput() {
  return <InputFields />
}

function PanelCalculate() {
  const { values } = useAppState()

  const propertyTax = currency(values.homeCost)
    .multiply(values.propertyTax / 100)
    .divide(12)

  const totalMonthlyPayment = currency(values.monthlyPayment)
    .add(propertyTax)
    .add(values.dues)
    .add(values.mortgageInsurance)
    .add(values.homeownerInsurance)
    .add(values.maintenance)

  const totalRentCost = getTotalRentCost(values.monthlyRent, values.rentRate, values.stayMonths)

  const buyCosts = getBuyTotals(values)

  const totalHomeEquity = getTotalEquity(values)
  const totalBuyNetCost = buyCosts.presentValue.subtract(totalHomeEquity)

  let summary = ''

  const costDiff = totalBuyNetCost.subtract(totalRentCost)

  if (costDiff > 0) {
    summary = `You will save ${asCurrency(costDiff)} if you rent instead of buy.`
  } else {
    summary = `You will save ${asCurrency(costDiff)} if you buy instead of rent.`
  }

  return (
    <CalculateResults>
      <table className="w-full text-xs md:text-sm">
        <thead className="bg-gray-100">
          <tr>
            <th className="py-3 px-2 text-left">Monthly Expenses</th>
            <th className="py-3 px-2 text-right">Rent</th>
            <th className="py-3 px-2 text-right">Buy</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-300">
          <tr>
            <td className="py-3 px-2">Monthly Payment:</td>
            <td className="py-3 px-2 font-bold text-right">{asCurrency(values.monthlyRent)}</td>
            <td className="py-3 px-2 font-bold text-right">{asCurrency(values.monthlyPayment)}</td>
          </tr>
          <tr>
            <td className="py-3 px-2">Property Tax:</td>
            <td className="py-3 px-2 text-right"></td>
            <td className="py-3 px-2 text-right">{asCurrency(propertyTax)}</td>
          </tr>
          <tr>
            <td className="py-3 px-2">Association Dues:</td>
            <td className="py-3 px-2 text-right"></td>
            <td className="py-3 px-2 text-right">{asCurrency(values.dues)}</td>
          </tr>
          <tr>
            <td className="py-3 px-2">Mortgage Insurance:</td>
            <td className="py-3 px-2 text-right"></td>
            <td className="py-3 px-2 text-right">{asCurrency(values.mortgageInsurance)}</td>
          </tr>
          <tr>
            <td className="py-3 px-2">Homeowner's Insurance:</td>
            <td className="py-3 px-2 text-right"></td>
            <td className="py-3 px-2 text-right">{asCurrency(values.homeownerInsurance)}</td>
          </tr>
          <tr>
            <td className="py-3 px-2">Other Costs:</td>
            <td className="py-3 px-2 text-right"></td>
            <td className="py-3 px-2 text-right">{asCurrency(values.maintenance)}</td>
          </tr>
          <tr>
            <th className="py-3 px-2 text-left">Total Monthly Payment:</th>
            <th className="py-3 px-2 text-right">{asCurrency(values.monthlyRent)}</th>
            <th className="py-3 px-2 text-right">{asCurrency(totalMonthlyPayment)}</th>
          </tr>
          <tr className="bg-gray-100">
            <th className="py-3 px-2 text-left">Total Monthly Costs</th>
            <th></th>
            <th></th>
          </tr>
          <tr>
            <td className="py-3 px-2">Present Value of All Costs Over {values.stayMonths} Months:</td>
            <td className="py-3 px-2 text-right">{asCurrency(totalRentCost)}</td>
            <td className="py-3 px-2 text-right">{asCurrency(buyCosts.presentValue)}</td>
          </tr>
          <tr>
            <td className="py-3 px-2">Home Equity:</td>
            <td className="py-3 px-2 text-right"></td>
            <td className="py-3 px-2 text-right">{asCurrency(totalHomeEquity)}</td>
          </tr>
          <tr>
            <td className="py-3 px-2">Total Net Cost:</td>
            <td className="py-3 px-2 font-bold text-right">{asCurrency(totalRentCost)}</td>
            <td className="py-3 px-2 font-bold text-right">{asCurrency(totalBuyNetCost)}</td>
          </tr>
        </tbody>
        <tfoot className="border-t border-gray-300">
          <tr>
            <th className="py-3 px-2 font-bold text-left" colSpan="3">
              {summary}
            </th>
          </tr>
        </tfoot>
      </table>
    </CalculateResults>
  )
}

function PanelChart() {
  const { values } = useAppState()

  return (
    <div className="py-4 space-y-4">
      <EquityBalanceInterestChart amortization={values.amortization} title="Rent vs. Buy Calculator" />
    </div>
  )
}

function PanelDetail() {
  const { values } = useAppState()

  return <AmortizationScheduleTable amortization={values.amortization} />
}

export default {
  name: 'Should I buy or rent my home?',
  tabs: [
    {
      name: 'Input',
      component: PanelInput,
      beforeTabChange: (dispatch, fields, values) => {
        const validate = validateFields(fields)

        if (validate.isValid) {
          const updatedValues = updateValuesFromFields(values, fields)

          const monthlyPayment = getMonthlyPayment(
            currency(updatedValues.homeCost).subtract(updatedValues.downPayment),
            updatedValues.interestRate,
            updatedValues.numPayments
          )

          const amortization = getFixedMortgageAmortization({
            principal: currency(updatedValues.homeCost).subtract(updatedValues.downPayment),
            interestRate: updatedValues.interestRate,
            numPayments: updatedValues.numPayments,
            payment: monthlyPayment,
            startDate: addMonths(normalizedDate(new Date()), 1),
          })

          dispatch({ type: 'setValues', payload: { ...updatedValues } })
          dispatch({ type: 'setValue', payload: { name: 'amortization', value: amortization } })
          dispatch({ type: 'setValue', payload: { name: 'monthlyPayment', value: monthlyPayment } })
        }

        dispatch({ type: 'setFields', payload: validate.fields })

        return validate.isValid
      },
    },
    {
      name: 'Calculate',
      component: PanelCalculate,
    },
    {
      name: 'Chart',
      component: PanelChart,
    },
    {
      name: 'Detail',
      component: PanelDetail,
    },
  ],
  fields: [
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'monthlyRent',
      label: 'Monthly Rent',
      value: 600,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'homeCost',
      label: 'Home Cost',
      value: 120000,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'downPayment',
      label: 'Down Payment',
      value: 20000,
    },
    {
      type: 'number',
      dataType: 'int',
      name: 'numPayments',
      label: 'Loan Term (Months)',
      value: 360,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'points',
      label: 'Points',
      value: 0,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'fees',
      label: 'Fees',
      value: 400,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'mortgageInsurance',
      label: 'Monthly Mortgage Insurance',
      value: 10,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'homeownerInsurance',
      label: 'Homeowner Insurance',
      value: 25,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'dues',
      label: 'Association Dues (if any)',
      value: 150,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'maintenance',
      label: 'Average Monthly Maintenance',
      value: 100,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'propertyTax',
      label: 'Property Tax',
      value: 2,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'incomeTax',
      label: 'State and Federal Income Tax',
      value: 28,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'interestRate',
      label: 'Interest Rate',
      value: 6.9,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'savingsRate',
      label: 'Your Savings Rate',
      value: 3.9,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'appreciationRate',
      label: 'How Much Will Your Home Appreciate Each Year?',
      value: 5,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'rentRate',
      label: 'How Much Will Rent Increase Each Year?',
      value: 5,
    },
    {
      type: 'number',
      dataType: 'int',
      name: 'stayMonths',
      label: 'How Long Will You Stay In This Property? (Months)',
      value: 60,
    },
  ],
}
